import ahoyTrack from 'src/explore/services/ahoy';

const existingProperties = ( object: Object ) => {
  const found = {};

  Object.keys( object ).forEach(( key: string ) => {
    if ( object[key as keyof object] != null ) {
      found[key as keyof object] = object[key as keyof object];
    }
  });

  return found;
};

const trackEvent = (
  event: string,
  properties: Record<string, any>,
  removeEmptyProperties: boolean = true
) => {
  const propertiesForEvent = Object.assign( properties, { page: window.location.pathname });
  ahoyTrack(
    event,
    removeEmptyProperties ? existingProperties( propertiesForEvent ) : propertiesForEvent
  );
};

const trackClick = ( properties: Record<string, any> ) => {
  trackEvent( '$click', properties );
};

// Mirrors the "Cart Viewed" event GBX fires (src/services/Analytics/index.ts) so the
// whitelabel storefronts emit the same ahoy event with the same payload shape.
const trackCartViewed = ( cart: Record<string, any> | null | undefined ) => {
  if ( !cart ) {
    return;
  }

  const cartItems = ( cart.subcarts || []).reduce(
    ( items: Record<string, any>[], subcart: Record<string, any> ) => {
      ( subcart.cart_items || []).forEach(( item: Record<string, any> ) => {
        const dataForAnalytics = item.data_for_analytics || {};

        items.push({
          product_id: dataForAnalytics.product_id,
          quantity: item.quantity,
          per_unit_discounted_price_in_cents: item.per_unit_discounted_price_in_cents,
          per_unit_undiscounted_price_in_cents: item.per_unit_undiscounted_price_in_cents,
          options: ( item.customer_options || []).map(( option: Record<string, any> ) => ({
            option_id: option.option_id,
            variance_id: option.variance_id,
            name: option.name,
            summary: option.summary,
          })),
        });
      });

      return items;
    },
    []
  );

  trackEvent( 'Cart Viewed', {
    number_of_items: cart.number_of_items,
    total_in_cents: cart.total_in_cents,
    shipping_subtotal_in_cents: cart.shipping_subtotal_in_cents,
    promotion_discount_in_cents: cart.promotion_discount_in_cents,
    cart_items: cartItems,
  });
};

const trackClicks = ( selector: string ) => {
  // properties can be added or overridden using data-analytics attributes
  // on $click targets with semicolon separated `key: value` data
  // e.g. "position: 1; text: donut"
  const specialProperties = ( data: string ) => {
    const cast = ( value: string ) => {
      if ( /^true$/i.test( value )) {
        return true;
      }
      if ( /^false$/i.test( value )) {
        return false;
      }
      if ( /^-?\d*\.?\d+$/.test( value )) {
        return Number( value );
      }
      return value;
    };

    const entries: {
      [key: string]: boolean | number | string;
    } = {};

    if ( data ) {
      data.split( ';' ).forEach(( datum ) => {
        const entry = datum.split( ':' );
        if ( entry[1]) {
          entries[entry[0].trim()] = cast( entry[1].trim());
        }
      });
    }

    return entries;
  };

  document.addEventListener( 'click', ( event: any ) => {
    const tracked = event.target.closest( selector );

    if ( tracked && tracked.className.indexOf( 'no-track' ) === -1 ) {
      trackClick({
        class: tracked.className || null,
        href: tracked.href,
        id: tracked.id || null,
        section: tracked.closest( '[data-section]' )?.getAttribute( 'data-section' ),
        tag: tracked.tagName.toLowerCase(),
        text:
          tracked instanceof HTMLInputElement
            ? tracked.value
            : ( tracked.textContent || tracked.innerText || tracked.innerHTML )
                .replace( /[\s\r\n]+/g, ' ' )
                .trim(),
        ...specialProperties( tracked.dataset.analytics ),
      });
    }
  });
};

export default {
  trackCartViewed,
  trackClick,
  trackClicks,
  trackEvent,
};
